import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface KlingV1Params {
  /**
   * 视频生成提示词，必须提供且长度在1-2500字符之间
   */
  prompt: string;
  /**
   * 视频时长（秒），可选值为5秒或10秒
   * @values 5 | 10
   */
  duration?: 5 | 10;
  /**
   * 视频宽高比，支持16:9、9:16、1:1
   * @values 16:9 | 9:16 | 1:1
   */
  aspect_ratio?: '16:9' | '9:16' | '1:1';
  /**
   * 负面提示词
   */
  negative_prompt?: string;
  /**
   * 提示词相关性强度
   */
  cfg_scale?: number;
  /**
   * 生成模式，标准(std)或专业(pro)，默认std
   * @default "std"
   * @values std | pro
   */
  mode?: 'std' | 'pro';
  /**
   * 控制摄像机运动的参数（如果未指定，模型将根据输入文本/图像智能匹配）
   */
  camera_control?: {
    /**
     * 预定义摄像机运动类型 - simple: 基础摄像机运动，在此类型下可在config中选择6个选项之一 - down_back: 摄像机下降并向后移动（向下平移并缩小） - forward_up: 摄像机向前移动并向上倾斜（放大并向上平移） - right_turn_forward: 向右旋转并向前移动 - left_turn_forward: 向左旋转并向前移动
     * @values simple | down_back | forward_up | right_turn_forward | left_turn_forward
     */
    type?: 'simple' | 'down_back' | 'forward_up' | 'right_turn_forward' | 'left_turn_forward';
    /**
     * 包含6个字段，用于指定摄像机在不同方向上的运动或变化。当type设置为simple时为必填，其他类型时应留空。要求6个参数中只能有一个非零，其余必须为零。字段包括：horizontal (水平移动)、vertical (垂直移动)、pan (平移)、tilt (俯仰)、roll (旋转)、zoom (缩放)
     */
    config?: Record<string, any>;
  };
}

export interface KlingV1Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_v1
 *
 * 通过Kling AI V1 API创建视频生成任务。[API Documentation](https://api.klingai.com)
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingV1 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingV1({
 *   // 参数见 KlingV1Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingV1Params 接口定义
 * @returns 生成的资源列表
 */
export async function klingV1(
  params: KlingV1Params,
): Promise<KlingV1Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_v1',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingV1Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
